import type { ResponsePrepTime } from "@memorang/types";
import { useRef } from "react";
import { useDevice } from "../../../../useDevice";
import AudioPlayerContainer, {
	type AudioPlayerRef,
} from "../../../AudioPlayerContainer";
import Box from "../../../Box";
import usePrepSequence from "../../hooks/usePrepSequence";
import InlineBlankFields, { type InlineBlankField } from "../InlineBlankFields";
import { ItemInstruction } from "./pte/ItemInstruction";
import { PrepCountdownBanner } from "./pte/PrepCountdownBanner";
import { SubInstruction } from "./pte/SubInstruction";

type Props = {
	audioUri: string;
	instruction: string;
	subInstruction?: string;
	stem: string;
	fields: InlineBlankField[];
	values?: Record<string, string>;
	defaultValues?: Record<string, string>;
	onChangeValues?: (values: Record<string, string>) => void;
	disabled?: boolean;
	prepTime?: ResponsePrepTime;
	allowAudioReplay?: boolean;
	onAudioEnded?: () => void;
	correctValues?: Record<string, string>;
	isValueCorrect?: (value: string, correctValue: string) => boolean;
};

const FillInTheBlanksTypeIn = ({
	audioUri,
	instruction,
	subInstruction,
	stem,
	fields,
	values,
	defaultValues,
	onChangeValues,
	disabled,
	prepTime,
	allowAudioReplay,
	onAudioEnded,
	correctValues,
	isValueCorrect,
}: Props) => {
	const { isMobile } = useDevice();
	const audioPlayerRef = useRef<AudioPlayerRef | null>(null);
	const hasAudioStimulus = Boolean(audioUri);
	const isTimed = prepTime != null;
	const playOnce = !allowAudioReplay;

	const { remainingPrepSeconds, showBanner } = usePrepSequence({
		prepTime,
		hasAudioStimulus,
		hasRecorder: false,
		audioPlayerRef,
		onAudioEnded,
		enabled: isTimed && !disabled,
	});

	return (
		<Box gap={isMobile ? 16 : 24}>
			<Box gap={8}>
				<ItemInstruction instruction={instruction} />
				{subInstruction ? <SubInstruction text={subInstruction} /> : null}
				{showBanner ? (
					<PrepCountdownBanner
						remainingSeconds={remainingPrepSeconds}
						playOnce={playOnce}
					/>
				) : null}
				{hasAudioStimulus ? (
					<AudioPlayerContainer
						ref={audioPlayerRef}
						uri={audioUri}
						playOnce={playOnce}
					/>
				) : null}
			</Box>
			<Box paddingHorizontal={isMobile ? 16 : 0}>
				<InlineBlankFields
					stemText={stem}
					fields={fields}
					values={values}
					defaultValues={defaultValues}
					onChangeValues={onChangeValues}
					disabled={disabled}
					correctValues={correctValues}
					isValueCorrect={isValueCorrect}
				/>
			</Box>
		</Box>
	);
};

export default FillInTheBlanksTypeIn;
